import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { deleteBannerThunk, getBannerListThunk, setMassSortBannerThunk } from 'redux/thunks/banner';
import { RootState, useDispatch, useSelector } from '../../redux/store';
import { DroppableDataTable } from 'components/DroppableDataTable';
import { TableHead } from 'components/table/TableHeadCustom';
import { setBannerListParams } from 'redux/slices/banner';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { ListHeader } from 'components/ListHeader/ListHeader';
import { BannerTableRow } from 'sections/banner';
import { PATH_DASHBOARD } from '../../routes/paths';
import { Banner } from 'types/banner';
import { LoadingScreen } from 'components/LoadingScreen';
import { useDialog } from 'hooks/useDialog';

const TABLE_HEAD: TableHead = {
  image: { label: 'Фото' },
  title: { label: 'Название' },
  text: { label: 'Описание' },
  controls: { label: '' },
};

export const BannerList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const bannerList = useSelector((state: RootState) => state.banner.bannerList);
  const bannerListParams = useSelector((state: RootState) => state.banner.bannerListParams);

  const { dialog, dialogDispatch, onOpen, onClose } = useDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [bannerListDroppable, setBannerListDroppable] = useState<Banner[]>(bannerList?.data || []);
  const [isLoaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const result = await dispatch(getBannerListThunk(bannerListParams));

      if (result && result.data) setBannerListDroppable(result.data);

      setLoaded(true);
    };

    load();
  }, [dispatch, bannerListParams]);

  const handleDeleteRow = useCallback(
    async (id: number) => {
      await dispatch(deleteBannerThunk(id));
      onClose();
      enqueueSnackbar(`Баннер ${id} удален`);

      const res = await dispatch(getBannerListThunk(bannerListParams));

      setBannerListDroppable(res?.data || []);
    },
    [bannerListParams, dispatch, enqueueSnackbar, onClose]
  );

  const onDeleteRowClickHandler = useCallback(
    (id: number) => {
      dialogDispatch({
        type: 'ALL',
        payload: {
          ...dialog,
          main: `Вы действительно хотите удалить баннер ${id}?`,
          footer: () => handleDeleteRow(id),
        },
      });

      onOpen();
    },
    [dialog, dialogDispatch, handleDeleteRow, onOpen]
  );

  const handleEditRow = useCallback(
    (id: number) => {
      navigate(PATH_DASHBOARD.banner.info(id));
    },
    [navigate]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(
        setBannerListParams({
          ...bannerListParams,
          page,
        })
      );
    },
    [dispatch, bannerListParams]
  );

  const handleSaveItems = useCallback(
    (items: any[]) => {
      const paramSortedList = items.map((item, index) => ({
        id: item.id,
        sort: index + 1,
      }));

      dispatch(setMassSortBannerThunk(paramSortedList));
    },
    [dispatch]
  );

  const banners =
    bannerListDroppable &&
    bannerListDroppable.map((row, index) => (
      <BannerTableRow
        key={row.id}
        row={row}
        onDeleteRow={onDeleteRowClickHandler}
        onEditRow={handleEditRow}
        index={index}
      />
    ));

  return (
    <>
      {isLoaded ? (
        <>
          <ListHeader link={{ name: 'Баннер', href: PATH_DASHBOARD.banner.new }} />

          {bannerList.data && bannerListDroppable.length && (
            <DroppableDataTable
              tableHead={TABLE_HEAD}
              pagination={bannerList.meta}
              params={bannerListParams}
              onPage={handlePageChange}
              items={bannerListDroppable}
              onSetItems={setBannerListDroppable}
              saveCallback={handleSaveItems}
            >
              {banners}
            </DroppableDataTable>
          )}
        </>
      ) : (
        <LoadingScreen />
      )}

      <ConfirmDialog dialog={dialog} open={dialog.open} onClose={onClose} />
    </>
  );
};
